<template>
  <div class="mx-auto w-full max-w-screen-2xl xs:p-0 xl:px-4 xl:py-8">
    <SFHeadline
      tag="h1"
      class="flex justify-center pt-8 !font-rubik text-gray-700"
      size="3xl"
      data-testid="empty-state-headline"
    >
      {{ $t('brands_page.title') }}
    </SFHeadline>

    <div class="space-y-6">
      <div
        class="grid grid-cols-1 border-b border-gray-200 pb-6 last:border-b-0 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 lg:p-8 xl:grid-cols-6 2xl:grid-cols-6 2xl:p-8"
      >
        <div
          v-for="(categories, letter) in groupedCategories"
          :key="letter"
          class="space-y-4 border-b border-gray-200 pb-6 last:border-b-0 xs:p-8"
        >
          <h2 class="mb-6 font-rubik text-xl font-semibold text-gray-600">
            {{ letter }}
          </h2>
          <div class="space-y-1">
            <NuxtLink
              v-for="category in categories"
              :key="category.id"
              :to="buildSingleBrandPath(category)"
              class="block cursor-pointer font-rubik text-lg text-gray-600 transition-colors hover:text-orange-500"
            >
              {{ category.name }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { definePageMeta } from '#imports'
import { NuxtLink } from '#components'
import { useCategoryByPath } from '#storefront/composables'
import type { Category } from '#storefront/types'
import { SFHeadline } from '#storefront-ui/components'
import { useRouteHelpers } from '~/composables'
import { useTopNavigationMenu } from '~/composables/useTopNavigationMenu'

const { buildSingleBrandPath } = useRouteHelpers()

// ✅ now passed from [...slug].vue
const props = defineProps<{ gender: string | null }>()
const { t } = useI18n()
const brands = t('brands_page.title')

const { currentMenu, setCurrentMenu } = useTopNavigationMenu()

onMounted(() => {
  if (props.gender && props.gender !== currentMenu.value.toLowerCase()) {
    setCurrentMenu(props.gender)
  }
})

const currentGender = computed(() => {
  if (props.gender) {
    return props.gender
  }
  if (currentMenu.value !== 'Home') {
    return currentMenu.value
  }
  return 'Home'
})

const { data } = useCategoryByPath({
  params: {
    path:
      currentGender.value !== 'Home'
        ? `${currentGender.value.toLowerCase()}/${brands.toLowerCase()}`
        : brands.toLowerCase(),
  },
})

// Group categories by their first letter
const separateChildrenByLetter = (children: Category[] | undefined) => {
  if (!children?.length) {
    return {}
  }

  const grouped: Record<string, Category[]> = {}

  children.forEach((category) => {
    const name = category.name?.trim()
    if (!name) {
      return
    }

    const firstLetter = name[0].toUpperCase()
    const groupKey = /\d/.test(firstLetter) ? '0-9' : firstLetter

    if (!grouped[groupKey]) {
      grouped[groupKey] = []
    }
    grouped[groupKey].push(category)
  })

  return Object.fromEntries(
    Object.keys(grouped)
      .sort((a, b) => {
        const aIsSpecial = !/[A-Z0-9]/.test(a)
        const bIsSpecial = !/[A-Z0-9]/.test(b)
        if (aIsSpecial !== bIsSpecial) {
          return aIsSpecial ? -1 : 1
        }
        if (a === '0-9' || b === '0-9') {
          return a === '0-9' ? -1 : 1
        }
        return a.localeCompare(b)
      })
      .map((key) => [key, grouped[key]]),
  )
}

const groupedCategories = computed(() => {
  return separateChildrenByLetter(data.value?.children)
})

definePageMeta({
  pageType: 'all_brand_page',
  contentType: 'Brand Page',
  SnPageType: 'brand',
})
</script>
